import type { CatalogProductStatus } from '../services/api';

/**
 * Store Product Browser visibility rules.
 *
 * The catalog groups `discontinued` with `archived`, so an Active-only query
 * drops a product the moment it is discontinued — even when the store is still
 * pushing it to Shopify. A live storefront listing disappearing from the
 * browser is worse than showing a dead SKU, so the store default keeps
 * discontinued rows that are still in the sync selection.
 */

/** `placeholder` is a store's own submitted product (flag `store_browser_all_synced`), which reads as active. */
export type CatalogLifecycleStatus = 'active' | 'archived' | 'discontinued' | 'placeholder';

export interface StoreCatalogRow {
  id: string;
  status?: CatalogLifecycleStatus;
  /** Sync price verification (flag `sync_price_verification_alerts`). */
  pricing?: {
    verification?: {
      status?: 'verified' | 'unverified';
      lastVerifiedAt?: string | Date | null;
      lastVerifiedPrice?: number;
      unverifiedSince?: string | Date | null;
      reason?: 'fetch_failed' | 'not_in_results' | 'discontinued';
    };
    map?: number | null;
    mapStartAt?: string | Date | null;
    mapEndAt?: string | Date | null;
  };
}

/** Rows from an Active-only query carry no status; treat those as active. */
const statusOf = (product: StoreCatalogRow): CatalogLifecycleStatus => product.status || 'active';

export const isDiscontinuedInSync = (
  product: StoreCatalogRow,
  syncSelectedIds: Set<string>,
): boolean => statusOf(product) === 'discontinued' && syncSelectedIds.has(product.id);

const toTime = (value: string | Date | null | undefined): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * A MAP live now and dated through a future day is a confirmed price, so the
 * row is not "not rechecked" even before the daily sweep clears the mark.
 * Mirrors `liveMapValidThrough` in syncPriceVerificationAlertService.
 */
export const hasLiveDatedMap = (product: StoreCatalogRow, at: number = Date.now()): boolean => {
  const map = product.pricing?.map;
  if (typeof map !== 'number' || !Number.isFinite(map) || map <= 0) return false;
  const end = toTime(product.pricing?.mapEndAt);
  if (end === null || end <= at) return false;
  const start = toTime(product.pricing?.mapStartAt);
  return start === null || start <= at;
};

/**
 * A synced product whose price could not be re-verified by the last daily
 * refresh — reason `fetch_failed` or `not_in_results` on
 * `pricing.verification`, unless a live MAP dated through a future day
 * covers it (`hasLiveDatedMap`). A discontinued lifecycle status or
 * `reason: 'discontinued'` is not counted here: that row is already covered
 * by `isDiscontinuedInSync` and its own chip (status and reason update on
 * different clocks, so both must be excluded), so a product is never
 * double-counted between the two.
 */
export const isPriceUnverifiedInSync = (
  product: StoreCatalogRow,
  syncSelectedIds: Set<string>,
): boolean =>
  statusOf(product) !== 'discontinued' &&
  !hasLiveDatedMap(product) &&
  product.pricing?.verification?.status === 'unverified' &&
  product.pricing.verification.reason !== 'discontinued' &&
  syncSelectedIds.has(product.id);

/** Active, or discontinued but still selected for sync. Archived stays hidden. */
export const isVisibleInStoreDefault = (
  product: StoreCatalogRow,
  syncSelectedIds: Set<string>,
): boolean => statusOf(product) === 'active' || statusOf(product) === 'placeholder' || isDiscontinuedInSync(product, syncSelectedIds);

/**
 * Widen an Active request so discontinued rows come back and can be narrowed to
 * the in-sync ones client-side. Explicit Archived/All picks are passed through.
 */
export const storeRequestCatalogStatus = (
  catalogStatus: CatalogProductStatus,
): CatalogProductStatus => (catalogStatus === 'active' ? 'all' : catalogStatus);

/**
 * Apply the default-view rule to a widened result set. Only meaningful while the
 * Status pill is on Active; Archived/All are already explicit choices.
 */
export const filterStoreCatalogRows = <T extends StoreCatalogRow>(
  products: T[],
  catalogStatus: CatalogProductStatus,
  syncSelectedIds: Set<string>,
): T[] =>
  catalogStatus === 'active'
    ? products.filter((product) => isVisibleInStoreDefault(product, syncSelectedIds))
    : products;

export const discontinuedInSyncRows = <T extends StoreCatalogRow>(
  products: T[],
  syncSelectedIds: Set<string>,
): T[] => products.filter((product) => isDiscontinuedInSync(product, syncSelectedIds));
